/**
 * QActionCard Component (v2.1 - Icon Visibility Fix)
 * Professional clickable action cards matching platform design
 *
 * Fixes v2.1:
 * - Fixed icon visibility with lighter background
 * - Larger emoji icons for better readability
 * - Improved hover states with green accent
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * QActionCard Component
 * @param {string} id - Action identifier
 * @param {string} icon - Emoji icon
 * @param {string} title - Card title
 * @param {string} desc - Card description
 * @param {function} onClick - Click handler
 */
const QActionCard = ({ id, icon, title, desc, onClick }) => {
  return (
    <Box
      onClick={() => onClick(id)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        padding: '18px',
        backgroundColor: 'rgba(41, 43, 47, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        minWidth: '200px',
        flex: '1 1 auto',
        maxWidth: '300px',
        '&:hover': {
          backgroundColor: 'rgba(41, 43, 47, 0.9)',
          borderColor: 'rgba(39, 181, 109, 0.4)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          '& .action-icon-container': {
            backgroundColor: 'rgba(39, 181, 109, 0.2)',
            borderColor: 'rgba(39, 181, 109, 0.4)'
          }
        },
        '&:active': {
          transform: 'translateY(0)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      {/* Icon Container - Light background for emoji visibility */}
      <Box
        className="action-icon-container"
        sx={{
          width: 44,
          height: 44,
          borderRadius: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.5rem',
          lineHeight: 1,
          flexShrink: 0,
          transition: 'all 0.2s ease'
        }}
      >
        {icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: '0.9375rem',
            marginBottom: '4px',
            lineHeight: 1.3
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.55)',
            fontSize: '0.8125rem',
            lineHeight: 1.4
          }}
        >
          {desc}
        </Typography>
      </Box>
    </Box>
  );
};

export default QActionCard;
